// services/nflService.js
class NFLService {
  constructor() {
    this.baseURL = 'https://v1.american-football.api-sports.io';
    this.apiKey = process.env.REACT_APP_FOOTBALL_API_KEY; // Same API key as football
    this.debug = false;
    this.cache = {};
    
    if (!this.apiKey) {
      console.warn('🏈 NFL API key not found in environment variables');
    }
  }

  // Method to manually set API key if env vars aren't working
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 });
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[NFL API] ${message}`, data || ''); 
    }
  }

  // Cache management
  getCacheKey(endpoint, params) {
    return `nfl_${endpoint}_${JSON.stringify(params)}`;
  }

  setCache(key, data, ttl = 300000) { // 5 minutes default TTL
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }
    
    return cached.data;
  }

  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  async makeRequest(endpoint, params = {}) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);

    if (!this.apiKey) {
      // API key not configured - provide helpful error without exposing sensitive details
      //
      // Debug logging below is commented out to avoid exposing environment variable details in production.
      // Uncomment during local debugging if needed to troubleshoot API key configuration issues.
      //
      // this.log('API Key Debug Info:', {
      //   envVar: process.env.REACT_APP_FOOTBALL_API_KEY,
      //   envVarType: typeof process.env.REACT_APP_FOOTBALL_API_KEY,
      //   envVarLength: process.env.REACT_APP_FOOTBALL_API_KEY ? process.env.REACT_APP_FOOTBALL_API_KEY.length : 0,
      //   allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      // });

      throw new Error(`NFL API key not configured.

Please configure the API key:
1. Add REACT_APP_FOOTBALL_API_KEY to your .env file (same level as package.json)
2. Restart your development server after adding the variable
3. Or use NFLService.setApiKey('your_key_here') as a temporary workaround

For debugging: Uncomment the debug logging in nflService.js:68-76 to see detailed environment info.`);
    }

    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'v1.american-football.api-sports.io',
          'x-rapidapi-key': this.apiKey
        }
      });

      this.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`Error response:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      this.log(`Response data:`, data);

      // Check for API errors in response
      if (data.errors && data.errors.length > 0) {
        this.log(`API errors:`, data.errors);
        throw new Error(`API Error: ${data.errors.join(', ')}`);
      }

      // Cache successful responses with different TTLs based on data type
      let ttl = 300000; // 5 minutes default
      if (endpoint.includes('/games')) ttl = 30000; // 30 seconds for live games
      if (endpoint.includes('/standings')) ttl = 3600000; // 1 hour for standings
      if (endpoint.includes('/leagues') || endpoint.includes('/seasons')) ttl = 86400000; // 24 hours for static data

      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.log(`Request failed:`, error.message);
      console.error('NFL API request failed:', error);
      throw error;
    }
  }

  // CORE ENDPOINTS

  // Get timezone list
  async getTimezones() {
    return await this.makeRequest('/timezone');
  }

  // Get seasons
  async getSeasons() {
    return await this.makeRequest('/seasons');
  }

  // Get leagues/competitions
  async getLeagues(id = null, season = null, current = null) {
    const params = {};
    if (id) params.id = id;
    if (season) params.season = season;
    if (current) params.current = current;
    return await this.makeRequest('/leagues', params);
  }

  // Get teams
  async getTeams(id = null, league = null, season = null, name = null, code = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (league) params.league = league;
    if (season) params.season = season;
    if (name) params.name = name;
    if (code) params.code = code;
    if (search) params.search = search;

    // This endpoint requires at least one parameter
    if (!id && !league && !season && !name && !code && !search) {
      throw new Error('getTeams requires at least one parameter');
    }

    return await this.makeRequest('/teams', params);
  }

  // Get players
  async getPlayers(id = null, name = null, team = null, season = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (team) params.team = team;
    if (season) params.season = season;
    if (search) params.search = search;

    // This endpoint requires at least one parameter
    if (!id && !name && !team && !season && !search) {
      throw new Error('getPlayers requires at least one parameter');
    }

    return await this.makeRequest('/players', params);
  }

  // Get player statistics
  async getPlayerStatistics(id = null, team = null, season) {
    if (!season) {
      throw new Error('getPlayerStatistics requires season parameter');
    }
    const params = { season };
    if (id) params.id = id;
    if (team) params.team = team;

    // This endpoint requires at least season + (id or team)
    if (!id && !team) {
      throw new Error('getPlayerStatistics requires season and either id or team parameter');
    }

    return await this.makeRequest('/players/statistics', params);
  }

  // Get injuries
  async getInjuries(player = null, team = null) {
    const params = {};
    if (player) params.player = player;
    if (team) params.team = team;

    // This endpoint requires at least one parameter
    if (!player && !team) {
      throw new Error('getInjuries requires either player or team parameter');
    }

    return await this.makeRequest('/injuries', params);
  }

  // GAMES & MATCHES

  // Get games
  async getGames(id = null, date = null, league = null, season = null, team = null, h2h = null, live = null, timezone = null) {
    const params = {};
    if (id) params.id = id;
    if (date) params.date = date;
    if (league) params.league = league;
    if (season) params.season = season;
    if (team) params.team = team;
    if (h2h) params.h2h = h2h;
    if (live) params.live = live;
    if (timezone) params.timezone = timezone;

    // This endpoint requires at least one parameter
    if (!id && !date && !league && !season && !team && !h2h && !live) {
      throw new Error('getGames requires at least one parameter');
    }

    return await this.makeRequest('/games', params);
  }

  // Get today's games
  async getTodayGames(league = null, timezone = null) {
    const today = new Date().toISOString().split('T')[0];
    return await this.getGames(null, today, league, null, null, null, null, timezone);
  }

  // Get live games
  async getLiveGames() {
    return await this.getGames(null, null, null, null, null, null, 'all');
  }

  // Get game events
  async getGameEvents(gameId) {
    if (!gameId) {
      throw new Error('getGameEvents requires gameId parameter');
    }
    return await this.makeRequest('/games/events', { id: gameId });
  }

  // Get game team statistics
  async getGameTeamStatistics(gameId, team = null) {
    if (!gameId) {
      throw new Error('getGameTeamStatistics requires gameId parameter');
    }
    const params = { id: gameId };
    if (team) params.team = team;
    return await this.makeRequest('/games/statistics/teams', params);
  }

  // Get game player statistics
  async getGamePlayerStatistics(gameId, group = null, team = null, player = null) {
    if (!gameId) {
      throw new Error('getGamePlayerStatistics requires gameId parameter');
    }
    const params = { id: gameId };
    if (group) params.group = group;
    if (team) params.team = team;
    if (player) params.player = player;
    return await this.makeRequest('/games/statistics/players', params);
  }

  // STANDINGS

  // Get standings
  async getStandings(league, season, team = null, conference = null, division = null) {
    if (!league || !season) {
      throw new Error('getStandings requires league and season parameters');
    }
    const params = { league, season };
    if (team) params.team = team;
    if (conference) params.conference = conference;
    if (division) params.division = division;
    return await this.makeRequest('/standings', params);
  }

  // Get conferences
  async getConferences(league, season) {
    if (!league || !season) {
      throw new Error('getConferences requires league and season parameters');
    }
    return await this.makeRequest('/standings/conferences', { league, season });
  }

  // Get divisions
  async getDivisions(league, season) {
    if (!league || !season) {
      throw new Error('getDivisions requires league and season parameters');
    }
    return await this.makeRequest('/standings/divisions', { league, season });
  }

  // ODDS

  // Get odds
  async getOdds(game, bookmaker = null, bet = null) {
    if (!game) {
      throw new Error('getOdds requires game parameter');
    }
    const params = { game };
    if (bookmaker) params.bookmaker = bookmaker;
    if (bet) params.bet = bet;
    return await this.makeRequest('/odds', params);
  }

  // Get available bets
  async getBets(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/bets', params);
  }

  // Get bookmakers
  async getBookmakers(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/bookmakers', params);
  }

  // UTILITY FUNCTIONS

  // Get today's date in YYYY-MM-DD format (local timezone)
  getTodayDate() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Get current season (NFL seasons span two years, e.g., 2023 season runs 2023-2024)
  getCurrentSeason() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // JavaScript months are 0-indexed
    
    // NFL season typically starts in September and ends in February of next year
    // So if we're in Jan-July, we're in the previous year's season
    return month <= 7 ? year - 1 : year;
  }

  // NFL Week-to-Date mapping for 2025 season
  getWeekDateRange(weekId, seasonYear = 2025) {
    // Map for 2025 season
    const weekDates2025 = {
      // Regular season
      'week1': { start: '2025-09-04', end: '2025-09-08' },
      'week2': { start: '2025-09-11', end: '2025-09-15' },
      'week3': { start: '2025-09-18', end: '2025-09-22' },
      'week4': { start: '2025-09-25', end: '2025-09-29' },
      'week5': { start: '2025-10-02', end: '2025-10-06' },
      'week6': { start: '2025-10-09', end: '2025-10-13' },
      'week7': { start: '2025-10-16', end: '2025-10-20' },
      'week8': { start: '2025-10-23', end: '2025-10-27' },
      'week9': { start: '2025-10-30', end: '2025-11-03' },
      'week10': { start: '2025-11-06', end: '2025-11-10' },
      'week11': { start: '2025-11-13', end: '2025-11-17' },
      'week12': { start: '2025-11-20', end: '2025-11-24' },
      'week13': { start: '2025-11-27', end: '2025-12-01' },
      'week14': { start: '2025-12-04', end: '2025-12-08' },
      'week15': { start: '2025-12-11', end: '2025-12-15' },
      'week16': { start: '2025-12-18', end: '2025-12-22' },
      'week17': { start: '2025-12-24', end: '2025-12-29' },
      'week18': { start: '2026-01-03', end: '2026-01-04' },
      // Playoffs
      'wildcard': { start: '2026-01-10', end: '2026-01-12' },
      'divisional': { start: '2026-01-17', end: '2026-01-18' },
      'conference': { start: '2026-01-25', end: '2026-01-25' },
      'superbowl': { start: '2026-02-08', end: '2026-02-08' },
      // Preseason
      'preseason': { start: '2025-08-07', end: '2025-08-24' }
    };

    // Map for 2024 season (for reference/testing)
    const weekDates2024 = {
      'week1': { start: '2024-09-05', end: '2024-09-09' },
      'week2': { start: '2024-09-12', end: '2024-09-16' },
      'week3': { start: '2024-09-19', end: '2024-09-23' },
      'week4': { start: '2024-09-26', end: '2024-09-30' },
      'week5': { start: '2024-10-03', end: '2024-10-07' },
      'week6': { start: '2024-10-10', end: '2024-10-14' },
      'week7': { start: '2024-10-17', end: '2024-10-21' },
      'week8': { start: '2024-10-24', end: '2024-10-28' },
      'week9': { start: '2024-10-31', end: '2024-11-04' },
      'week10': { start: '2024-11-07', end: '2024-11-11' },
      'week11': { start: '2024-11-14', end: '2024-11-18' },
      'week12': { start: '2024-11-21', end: '2024-11-25' },
      'week13': { start: '2024-11-28', end: '2024-12-02' },
      'week14': { start: '2024-12-05', end: '2024-12-09' },
      'week15': { start: '2024-12-12', end: '2024-12-16' },
      'week16': { start: '2024-12-19', end: '2024-12-23' },
      'week17': { start: '2024-12-25', end: '2024-12-30' },
      'week18': { start: '2025-01-04', end: '2025-01-05' },
      'wildcard': { start: '2025-01-11', end: '2025-01-13' },
      'divisional': { start: '2025-01-18', end: '2025-01-19' },
      'conference': { start: '2025-01-26', end: '2025-01-26' },
      'superbowl': { start: '2025-02-09', end: '2025-02-09' },
      'preseason': { start: '2024-08-08', end: '2024-08-25' }
    };
    
    const weekDates = seasonYear === 2024 ? weekDates2024 : weekDates2025;
    return weekDates[weekId] || null;
  }

  // Get games for a specific week
  async getGamesForWeek(weekId, seasonYear = 2025, league = 1, team = null, timezone = 'America/New_York') {
    const dateRange = this.getWeekDateRange(weekId, seasonYear);
    if (!dateRange) {
      throw new Error(`Invalid week identifier: ${weekId}`);
    }

    try {
      // Build array of dates in the week range
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      const dates = [];

      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
      }

      // Fetch games with controlled concurrency to avoid rate limiting
      // Process in batches of 3 concurrent requests at a time
      const BATCH_SIZE = 3;
      const allGames = [];

      for (let i = 0; i < dates.length; i += BATCH_SIZE) {
        const batch = dates.slice(i, i + BATCH_SIZE);

        // Fetch this batch in parallel
        const promises = batch.map(dateStr =>
          this.getGames(null, dateStr, league, seasonYear, team, null, null, timezone)
            .then(response => response?.response || [])
            .catch(err => {
              console.warn(`Failed to fetch games for ${dateStr}:`, err);
              return []; // Return empty array on error, don't fail the whole week
            })
        );

        const batchResults = await Promise.all(promises);

        // Flatten batch results and add to all games
        allGames.push(...batchResults.flat());

        // Small delay between batches to be nice to the API (only if there are more batches)
        if (i + BATCH_SIZE < dates.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Sort by timestamp
      allGames.sort((a, b) => {
        const timestampA = a.game?.date?.timestamp || 0;
        const timestampB = b.game?.date?.timestamp || 0;
        return timestampA - timestampB;
      });

      return { response: allGames };
    } catch (error) {
      console.error(`Failed to fetch games for week ${weekId}:`, error);
      return { response: [], error: error.message };
    }
  }

  // Get the default date for games based on season status
  getDefaultGameDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    
    // If we're in the off-season (March-August), show the upcoming season opener
    if (month >= 3 && month <= 8) {
      // NFL season typically starts first Thursday after Labor Day (early September)
      // For 2025, season starts September 4th
      return `${year}-09-04`;
    }
    
    // Otherwise, show today's date
    return this.getTodayDate();
  }

  // Get season opener date for a given year
  getSeasonOpenerDate(year) {
    // NFL season typically starts first Thursday after Labor Day
    // These are approximate dates for each season
    const openerDates = {
      2023: '2023-09-07',
      2024: '2024-09-05', 
      2025: '2025-09-04'
    };
    
    return openerDates[year] || `${year}-09-07`;
  }

  // Get available seasons for current era (2023-2025)
  getAvailableSeasons() {
    return [2025, 2024, 2023];
  }

  // Get date range
  getDateRange(days = 7, startFromToday = true) {
    const dates = [];
    const startOffset = startFromToday ? 0 : -days;
    
    for (let i = startOffset; i < (startFromToday ? days : 0); i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  }

  // Get games for multiple seasons
  async getGamesForSeasons(date = null, seasons = [2025, 2024, 2023], league = 1, team = null, timezone = 'America/New_York') {
    try {
      const allGames = [];
      const errors = [];
      
      // Determine which seasons to query based on the date
      let seasonsToQuery = seasons;
      if (date) {
        const queryYear = parseInt(date.split('-')[0]);
        const queryMonth = parseInt(date.split('-')[1]);
        
        // For dates in Sept-Dec, query that year's season
        // For dates in Jan-Feb, query the previous year's season (playoffs)
        // For future dates (like 2025-09), only query that year
        if (queryMonth >= 9) {
          // Regular season (Sept-Dec)
          seasonsToQuery = seasons.filter(s => s === queryYear);
        } else if (queryMonth <= 2) {
          // Playoffs (Jan-Feb)
          seasonsToQuery = seasons.filter(s => s === queryYear - 1);
        } else {
          // Off-season - try current year
          seasonsToQuery = [this.getCurrentSeason()];
        }
        
        // If no matching season found, fallback to querying the closest season
        if (seasonsToQuery.length === 0) {
          seasonsToQuery = [queryYear];
        }
      }
      
      // Fetch games for each season in parallel
      const promises = seasonsToQuery.map(async (season) => {
        try {
          const response = await this.getGames(null, date, league, season, team, null, null, timezone);
          return response.response || [];
        } catch (err) {
          console.warn(`Failed to fetch games for season ${season}:`, err);
          errors.push({ season, error: err.message });
          return [];
        }
      });
      
      const results = await Promise.all(promises);
      
      // Combine all games and sort by date/time
      results.forEach(games => {
        allGames.push(...games);
      });
      
      // Sort games by timestamp (most recent first)
      allGames.sort((a, b) => {
        const timestampA = a.game?.date?.timestamp || 0;
        const timestampB = b.game?.date?.timestamp || 0;
        return timestampB - timestampA;
      });
      
      return {
        response: allGames,
        errors: errors.length > 0 ? errors : null,
        seasonsQueried: seasonsToQuery
      };
    } catch (error) {
      console.error('Failed to fetch games for seasons:', error);
      throw error;
    }
  }

  // Get upcoming dates with labels
  getUpcomingDates(days = 7) {
    const dates = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      let label;
      if (i === 0) label = 'Today';
      else if (i === 1) label = 'Tomorrow';
      else label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      
      dates.push({ value: dateStr, label });
    }
    return dates;
  }

  // Format time with timezone
  formatTime(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Format date
  formatDate(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  // Get full date time
  getFullDateTime(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Get status color for games
  getStatusColor(status) {
    const statusMap = {
      'NS': 'bg-gray-100 text-gray-800',       // Not Started
      'Q1': 'bg-green-100 text-green-800',     // First Quarter (In Play)
      'Q2': 'bg-green-100 text-green-800',     // Second Quarter (In Play)
      'Q3': 'bg-green-100 text-green-800',     // Third Quarter (In Play)
      'Q4': 'bg-green-100 text-green-800',     // Fourth Quarter (In Play)
      'OT': 'bg-yellow-100 text-yellow-800',   // Overtime (In Play)
      'HT': 'bg-blue-100 text-blue-800',       // Halftime (In Play)
      'FT': 'bg-slate-100 text-slate-800',     // Finished (Game Finished)
      'AOT': 'bg-slate-100 text-slate-800',    // After Over Time (Game Finished)
      'CANC': 'bg-red-100 text-red-800',       // Cancelled
      'PST': 'bg-yellow-100 text-yellow-800'   // Postponed
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  }

  // Get conference/division display name
  getConferenceName(conference) {
    const conferenceMap = {
      'American Football Conference': 'AFC',
      'National Football Conference': 'NFC'
    };
    return conferenceMap[conference] || conference;
  }

  // Test API connection
  async testConnection() {
    try {
      this.log('Testing API connection...');
      const timezonesResult = await this.getTimezones();
      this.log('Timezones test result:', timezonesResult);
      
      if (timezonesResult && timezonesResult.response) {
        this.log('✅ API connection successful');
        return { success: true, timezones: timezonesResult.response.slice(0, 10) };
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      this.log('❌ API connection failed:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Get sample data for testing
  async getSampleData() {
    try {
      this.log('Getting sample data...');
      
      // Try to get seasons first
      const seasons = await this.getSeasons();
      this.log('Sample seasons:', seasons);
      
      // Try to get leagues
      const leagues = await this.getLeagues();
      this.log('Sample leagues:', leagues);
      
      // Try to get current NFL teams
      const currentSeason = this.getCurrentSeason();
      const nflTeams = await this.getTeams(null, 1, currentSeason); // NFL = league 1
      this.log('Sample NFL teams:', nflTeams);
      
      return {
        seasons: seasons.response?.slice(-4) || [], // Last 4 seasons
        leagues: leagues.response || [],
        teams: nflTeams.response?.slice(0, 10) || [],
        currentSeason
      };
      
    } catch (error) {
      this.log('Error getting sample data:', error.message);
      throw error;
    }
  }

  // Debug method to check environment setup
  debugEnvironment() {
    const envInfo = {
      nodeEnv: process.env.NODE_ENV,
      apiKey: this.apiKey ? `Set (${this.apiKey.length} chars)` : 'Not set',
      apiKeyRaw: process.env.REACT_APP_FOOTBALL_API_KEY,
      allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
      envCount: Object.keys(process.env).length,
      cacheSize: Object.keys(this.cache).length,
      currentSeason: this.getCurrentSeason()
    };
    
    console.log('🏈 NFL Service Environment Debug:', envInfo);
    return envInfo;
  }
}

// Add a global method for easy testing in console
window.NFLService = new NFLService();

export default window.NFLService;